import React, { useState, useEffect } from "react";
import { createPortal } from "react-dom";

/**
 * InvoiceTotals
 *
 * En la tarjeta se ve UNA sola línea: el total y un botón de flecha.
 * El desglose (descuento, delivery, subtotal, impuestos) se construye
 * únicamente dentro del popup — no existe en el DOM de la tarjeta,
 * así que no hay forma de que se desborde ahí.
 *
 * No calcula nada. Todos los montos llegan ya calculados por props.
 *
 * Requiere sp-totals.css
 */
export default function InvoiceTotals({
   cartIsPointsOrder = false,
   invoiceTotalPoints = 0,
   formatProductPoints = (v) => v,

   invoiceTotalDiscount = 0,
   invoiceTotalDelivery = 0,
   checkDeliveryOrder = "",
   invoiceSubtotal = 0,
   taxesArrayAll = [],
   invoiceTotal = 0,

   className = "",
}) {
   const [showSheet, setShowSheet] = useState(false);

   // Escape para cerrar, y el fondo no hace scroll mientras está abierta.
   useEffect(() => {
      if (!showSheet) return;
      const onKey = (e) => e.key === "Escape" && setShowSheet(false);
      const prevOverflow = document.body.style.overflow;
      document.body.style.overflow = "hidden";
      document.addEventListener("keydown", onKey);
      return () => {
         document.body.style.overflow = prevOverflow;
         document.removeEventListener("keydown", onKey);
      };
   }, [showSheet]);

   const money = (v) => Number(v || 0).toFixed(2);

   /* ================= TARJETA — solo el total ================= */
   return (
      <>
         <div className={`sp-totals ${className}`}>
            {cartIsPointsOrder ? (
               <>
                  <span className="sp-totals-label">Total puntos</span>
                  <span className="sp-totals-amount sp-totals-amount--points" translate="no">
                     {formatProductPoints(invoiceTotalPoints)} pts
                  </span>
               </>
            ) : (
               <>
                  <span className="sp-totals-label">
                     Total <small>Incluye itbms</small>
                  </span>
                  <span className="sp-totals-amount" translate="no">
                     ${money(invoiceTotal)}
                  </span>
                  <button
                     type="button"
                     className="sp-totals-toggle"
                     aria-label="Ver desglose de la cuenta"
                     aria-expanded={showSheet}
                     onClick={() => setShowSheet(true)}
                  >
                     <i className="uil uil-angle-down" />
                  </button>
               </>
            )}
         </div>

         {/* ================= POPUP — el desglose completo ================= */}
         {showSheet && createPortal(
            <div className="sp-sheet-backdrop" onClick={() => setShowSheet(false)}>
               <div
                  className="sp-sheet"
                  role="dialog"
                  aria-modal="true"
                  aria-label="Desglose de la cuenta"
                  onClick={(e) => e.stopPropagation()}
               >
                  <div className="sp-sheet-grip" aria-hidden="true" />

                  <div className="sp-sheet-head">
                     <h6 className="sp-sheet-title">Desglose de la cuenta</h6>
                     <button
                        type="button"
                        className="sp-sheet-close"
                        aria-label="Cerrar"
                        onClick={() => setShowSheet(false)}
                     >
                        <i className="uil uil-times" />
                     </button>
                  </div>

                  <div className="sp-sheet-body">
                     <div className="sp-sheet-row">
                        <span className="sp-sheet-key">Descuento</span>
                        <span className="sp-sheet-val" translate="no">${money(invoiceTotalDiscount)}</span>
                     </div>

                     {checkDeliveryOrder === "delivery" ? (
                        <div className="sp-sheet-row">
                           <span className="sp-sheet-key">Delivery</span>
                           <span className="sp-sheet-val" translate="no">${money(invoiceTotalDelivery)}</span>
                        </div>
                     ) : null}

                     <div className="sp-sheet-row">
                        <span className="sp-sheet-key">Subtotal</span>
                        <span className="sp-sheet-val" translate="no">${money(invoiceSubtotal)}</span>
                     </div>

                     {taxesArrayAll.map((tax, idx) => (
                        tax.total.toFixed(3) > 0 ? (
                           <div className="sp-sheet-row" key={tax.tax ?? idx}>
                              <span className="sp-sheet-key">Impuesto {tax.tax}</span>
                              <span className="sp-sheet-val" translate="no">
                                 ${Number(tax.total || 0).toFixed(3)}
                              </span>
                           </div>
                        ) : null
                     ))}

                     <div className="sp-sheet-row sp-sheet-row--total">
                        <span className="sp-sheet-key">Total <small>Incluye itbms</small></span>
                        <span className="sp-sheet-val" translate="no">${money(invoiceTotal)}</span>
                     </div>
                  </div>
               </div>
            </div>,
            document.body
         )}
      </>
   );
}